import React, { useState, useEffect, useRef } from 'react';
import hljs from 'highlight.js';
import * as JsDiff from 'diff';
import 'highlight.js/styles/atom-one-dark.css';
import python from 'highlight.js/lib/languages/python';
import javascript from 'highlight.js/lib/languages/javascript';
import css from 'highlight.js/lib/languages/css';
import json from 'highlight.js/lib/languages/json';
import LoadingAnimation from '../ui/spinner';
import { Folder, File } from "lucide-react";
import '../../styles/fileUpload.css';
import fileService from '../../services/fileService';
import mlService from '../../services/mlService';
import { apiClient } from '../../services/api';

// Register the languages
hljs.registerLanguage('python', python);
hljs.registerLanguage('javascript', javascript);
hljs.registerLanguage('css', css);
hljs.registerLanguage('json', json);

const FileUpload = () => {
  const [fileContent, setFileContent] = useState('');
  const [fileType, setFileType] = useState('');
  const [key, setKey] = useState(0);
  const [geminiOutput, setGeminiOutput] = useState("");
  const [accuracyResults, setAccuracyResults] = useState("");
  const [loading, setLoading] = useState(false);
  const [isCodeRunning, setIsCodeRunning] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [showFileDialog, setShowFileDialog] = useState(false);
  const [bucketFiles, setBucketFiles] = useState([]);
  const [currentPath, setCurrentPath] = useState('');
  const [currentFileName, setCurrentFileName] = useState('');
  const [uploadStatus, setUploadStatus] = useState('');
  const [textFeedback, setTextFeedback] = useState('');
  const [codeOutputs, setCodeOutputs] = useState({});
  const codeRef = useRef(null);
  const geminiCodeRef = useRef(null);
  const editableCodeRef = useRef(null);
  const [feedback, setFeedback] = useState(''); 
  const [feedbackStatus, setFeedbackStatus] = useState(''); 
  const [geminiFeedbackResponse, setGeminiFeedbackResponse] = useState('');
  const [, setGeminiLoading] = useState(false);
  const [isTraining, setIsTraining] = useState(false);
  const [selectedDataset, setSelectedDataset] = useState(null);
  const [selectedDatasetStatus, setSelectedDatasetStatus] = useState('');

  useEffect(() => {
    if (showFileDialog) {
      fetchBucketFiles();
    }
    checkSelectedDataset();
  }, [showFileDialog]);

  const fetchBucketFiles = async () => {
    try {
      setLoading(true);

      const currentUserId = localStorage.getItem('userId');

      const filesData = await fileService.listFiles(currentUserId);
      setBucketFiles(filesData.files || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching bucket files:', error);
      setLoading(false);
    }
  };

  const checkSelectedDataset = async () => {
    try {
      const selectedData = await fileService.getSelectedDataset();
      if (selectedData.selected) {
        setSelectedDataset({
          filePath: selectedData.filePath,
          rowCount: selectedData.rowCount,
          columnCount: selectedData.columnCount,
          fileSize: selectedData.fileSize
        });
      }
    } catch (error) {
      console.log('No dataset currently selected');
    }
  };

  // Handle user feedback submission
  const handleFeedbackSubmit = async () => {
    if (!feedback.trim()) {
      setFeedbackStatus('Please provide your feedback.');
      return;
    }

    try {
      setGeminiLoading(true)
      // Send the feedback to backend for processing
      const response = await apiClient.post('/api/process_feedback', {
        feedback: feedback,
        gemini_output: geminiOutput
      });

      const processedFeedback = response.data.processed_feedback;

      setGeminiFeedbackResponse(processedFeedback);
      setFeedbackStatus('Feedback processed successfully!');
      setFeedback(''); 
    } catch (error) {
      console.error("Error processing feedback:", error);
      setFeedbackStatus('Error processing feedback. Please try again.');
    } finally {
      setGeminiLoading(false);
    }

    setTimeout(() => setFeedbackStatus(''), 3000);
  };

  const handleFileSelect = async (fileId, isDataset = false) => {
    try {
      setLoading(true);
      const currentUserId = localStorage.getItem('userId');

      // If it's a dataset, select it first
      if (isDataset) {
        try {
          const selectResult = await fileService.selectDataset(fileId, currentUserId);
          setSelectedDataset({
            filePath: selectResult.filePath,
            rowCount: selectResult.rowCount,
            columnCount: selectResult.columnCount,
            fileSize: selectResult.fileSize,
            filename: selectResult.filename
          });
          setSelectedDatasetStatus(`Dataset "${selectResult.filename}" selected successfully!`);
          setTimeout(() => setSelectedDatasetStatus(''), 3000);
        } catch (error) {
          console.error('Error selecting dataset:', error);
          setSelectedDatasetStatus('Error selecting dataset');
          setTimeout(() => setSelectedDatasetStatus(''), 3000);
          setLoading(false);
          return;
        }
      } else {
        // For code files, get the file content
        const fileData = await fileService.getFile(fileId, currentUserId);
        const { content, fileType } = fileData;

        setFileContent(content);
        setFileType(getFileType(fileType));
        setCurrentFileName(fileId);
        setKey((prevKey) => prevKey + 1);

        // Process with backend after loading the file
        try {
          const response = await apiClient.post('/api/process_file', {
            file_content: content
          });

          const { gemini_text, code_only, feedback } = response.data;

          // Set the outputs based on response
          if (code_only) {
            setGeminiOutput(code_only);
            setTextFeedback(feedback);
          } else {
            setGeminiOutput(gemini_text);
            setTextFeedback('');
          }

          await uploadGemini(gemini_text, fileId);
        } catch (error) {
          console.error("Error processing file:", error);
        }
      }

      setShowFileDialog(false);
      setLoading(false);
    } catch (error) {
      console.error('Error retrieving file:', error);
      setLoading(false);
    }
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = async (e) => {
        setFileContent(e.target.result);
        setCurrentFileName(file.name);
        setKey((prevKey) => prevKey + 1);
        setLoading(true);

        try {
          const response = await apiClient.post('/api/process_file', {
            file_content: e.target.result
          });

          const { gemini_text, code_only, feedback } = response.data;

          // Set the outputs based on response
          if (code_only) {
            setGeminiOutput(code_only);
            setTextFeedback(feedback);
          } else {
            setGeminiOutput(gemini_text);
            setTextFeedback('');
          }

          await uploadGemini(gemini_text, file.name);
          setLoading(false);
        } catch (error) {
          console.error("Error processing file:", error);
          setLoading(false);
        }
      };
      reader.readAsText(file);
      const extension = file.name.split('.').pop().toLowerCase();
      setFileType(getFileType(extension));
    }
  };

  const handleUploadToCloud = async () => {
    if (!fileContent || !currentFileName) {
      setUploadStatus('No file content or filename available');
      return;
    }

    try {
      setLoading(true);

      const currentUserId = localStorage.getItem('userId');

      const uploadResult = await fileService.uploadFile(
        isEditing ? editableCodeRef.current.innerText : fileContent,
        currentFileName,
        currentUserId
      );
      setUploadStatus('File uploaded successfully!');

      // If it's a CSV file, automatically select it as the dataset
      if (currentFileName.endsWith('.csv') && uploadResult.datasetId) {
        try {
          const selectResult = await fileService.selectDataset(uploadResult.datasetId, currentUserId);
          setSelectedDataset({
            filePath: selectResult.filePath,
            rowCount: selectResult.rowCount,
            columnCount: selectResult.columnCount,
            fileSize: selectResult.fileSize,
            filename: selectResult.filename
          });
          setSelectedDatasetStatus(`Dataset "${currentFileName}" selected automatically!`);
          setTimeout(() => setSelectedDatasetStatus(''), 3000);
        } catch (error) {
          console.error('Error selecting uploaded dataset:', error);
        }
      }

      setLoading(false);

      // Refresh the file list if the dialog is open
      if (showFileDialog) {
        fetchBucketFiles();
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      setUploadStatus('Error uploading file');
      setLoading(false);
    }

    // Clear the status message after 3 seconds
    setTimeout(() => setUploadStatus(''), 3000);
  };

  const uploadGemini = async (geminiText, accuracytext) => {
    try {
      const accuracyData = await mlService.calculateAccuracy(accuracytext, geminiText);
      const finalAccuracy = accuracyData.accuracy_percentage;
      if(finalAccuracy == null){
      setAccuracyResults(finalAccuracy);
        await mlService.uploadGeminiOutput(geminiText, 0);
      } else {
        setAccuracyResults(finalAccuracy);
        await mlService.uploadGeminiOutput(geminiText, finalAccuracy);
      }
    } catch(error) {
      console.error("Error uploading gemini output:", error);
    }
  };

  const run_code = async (code, sectionKey) => {
    try {
      // Set code running status to true for the current section
      setIsCodeRunning((prev) => ({ ...prev, [sectionKey]: true }));
      setCodeOutputs((prev) => ({ ...prev, [sectionKey]: "Loading..." }));

      // Run the code and capture the output
      const runResult = await mlService.runCode(code);

      setCodeOutputs((prev) => ({
        ...prev,
        [sectionKey]: {
          stdout: runResult.stdout || runResult.output || "",
          stderr: runResult.stderr || ""
        },
      }));
    } catch (error) {
      console.error("Error running code:", error);
      setCodeOutputs((prev) => ({ ...prev, [sectionKey]: "Error running the code" }));
    } finally {
      // Set code running status to false for the current section
      setIsCodeRunning((prev) => ({ ...prev, [sectionKey]: false }));
    }
  };

  const writeToTextFile = () => {
    const blob = new Blob([geminiOutput], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "geminiOutput.txt";
    document.body.appendChild(a);
    a.click();
    URL.revokeObjectURL(url);
  };

  const getFileType = (extension) => {
    switch (extension) {
      case 'py':
        return 'python';
      case 'js':
      case 'jsx':
        return 'javascript';
      case 'css':
        return 'css';
      case 'json':
        return 'json';
      case 'png':
        return 'plaintext';
      case 'csv':
        return 'csv';
      case 'xls':
      case 'xlsx':
        return 'plaintext'; 
      default:
        return 'plaintext';
    }
  };

  const handleEditToggle = () => {
    setIsEditing(!isEditing);
    if (isEditing) {
      setFileContent(editableCodeRef.current.innerText);
      setKey((prevKey) => prevKey + 1);
    }
  };

  const navigateFolder = (folderPath) => {
    setCurrentPath(folderPath);
  };

  // Training and generating TensorFlow logs
  const handleTrainAndLog = async () => {
    setIsTraining(true);
    try {
      await mlService.trainWithLogging();

      // Download the generated log
      const logData = await mlService.downloadLog('training_log.csv');
      const url = window.URL.createObjectURL(new Blob([logData]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'training_log.csv');
      document.body.appendChild(link);
      link.click();
    } catch (error) {
      console.error('Error during training or log download', error);
    }
    finally {
      setIsTraining(false);
    }
  };

  // Lets us use both highlight.js and js.diff in the improved code section
  // creates a map and then feeds the js.diff results to highlight.js
  const generateDiff = (original, improved) => {
    const diff = JsDiff.diffWords(original, improved);

    return diff.map((part, index) => {
      let style = {};
      let content = part.value;

      if (part.added) {
        style = { backgroundColor: '#98FB98', color: 'grey' };
      } else if (part.removed) {
        style = { backgroundColor: '#A42A04', color: 'grey' };
      } else {
        style = {color: '#C8C8C8'}
      }

      const highlightedContent = hljs.highlight(fileType, content).value;

      return (
        <span key={index} style={style}>
          <span dangerouslySetInnerHTML={{ __html: highlightedContent }} />
        </span>
      );
    });
  };

  useEffect(() => {
    if (fileContent && fileType) {
      if (codeRef.current && !isEditing) {
        hljs.highlightElement(codeRef.current);
      }
      if (editableCodeRef.current && isEditing) {
        hljs.highlightElement(editableCodeRef.current);
      }
    }
  }, [fileContent, fileType, key, isEditing, geminiOutput]);

    return (
    <div className="file-upload-container"> {/* Parent Wrapper - FIXES THE ERROR */}
      <h2 className="file-upload-title">Upload or Retrieve a File</h2>

      <div className="button-container">
        <label className="file-upload-input" style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}>
          Database Upload
          <input
            type="file"
            accept=".csv,.py,.js,.jsx,.css,.json,.txt,.png,.xls,.xlsx"
            onChange={handleFileChange}
            style={{ display: 'none' }} 
          />
        </label>

        <button
          className="retrieve-button"
          onClick={() => {
            setShowFileDialog(true);
            setCurrentPath('');
          }}
        >
          Browseeeeeeeeee Cloud Files
        </button>
      </div>

      {/* Selected Dataset Information */}
      {selectedDataset && (
        <div className="selected-dataset-container" style={{
          backgroundColor: '#f0f8ff',
          border: '2px solid #4CAF50',
          borderRadius: '8px',
          padding: '15px',
          marginTop: '20px',
          marginBottom: '20px'
        }}>
          <h3 style={{ color: '#4CAF50', margin: '0 0 10px 0' }}>✓ Selected Dataset</h3>
          <div style={{ fontSize: '14px', color: '#333' }}>
            <p style={{ margin: '5px 0' }}><strong>File:</strong> {selectedDataset.filename || 'selected_dataset.csv'}</p>
            <p style={{ margin: '5px 0' }}><strong>Rows:</strong> {selectedDataset.rowCount || 'Unknown'}</p>
            <p style={{ margin: '5px 0' }}><strong>Columns:</strong> {selectedDataset.columnCount || 'Unknown'}</p>
            <p style={{ margin: '5px 0' }}><strong>Size:</strong> {selectedDataset.fileSize ? (selectedDataset.fileSize / 1024).toFixed(2) + ' KB' : 'Unknown'}</p>
          </div>
        </div>
      )}

      {selectedDatasetStatus && (
        <div style={{
          backgroundColor: '#d4edda',
          color: '#155724',
          padding: '10px',
          borderRadius: '4px',
          marginBottom: '10px',
          border: '1px solid #c3e6cb'
        }}>
          {selectedDatasetStatus}
        </div>
      )}

      {showFileDialog && (
        <div className="file-dialog-overlay">
          <div className="file-dialog">
            <h3>Cloud Storage Files</h3>
            {loading ? (
              <LoadingAnimation />
            ) : (
              <div className="file-browser">
                {currentPath && (
                  <div
                    className="file-item"
                    onClick={() => navigateFolder(currentPath.split('/').slice(0, -2).join('/') + '/')}
                  >
                    <Folder className="w-4 h-4 mr-2" />
                    ..
                  </div>
                )}
                {bucketFiles.length === 0 ? (
                  <div className="empty-message">No files found in this location</div>
                ) : (
                  <div className="file-list">
                    {bucketFiles.map((file) => {
                      const isCSV = file.name && file.name.endsWith('.csv');
                      return (
                        <div
                          key={file.datasetId || file.name}
                          className="file-item"
                          onClick={() => {
                            if (file.isDirectory) {
                              navigateFolder(file.name);
                            } else if (isCSV) {
                              handleFileSelect(file.datasetId || file.name, true);
                            } else {
                              handleFileSelect(file.datasetId || file.name, false);
                            }
                          }}
                        >
                          {file.isDirectory ? (
                            <Folder className="w-4 h-4 mr-2" />
                          ) : (
                            <File className="w-4 h-4 mr-2" />
                          )}
                          <span>{file.name.split('/').pop()}</span>
                          {isCSV && <span className="dataset-badge"> (Dataset)</span>}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
            <button
              className="close-dialog"
              onClick={() => setShowFileDialog(false)}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {fileContent && (
        <div key={key}>
          <div className="code-comparison-container">
            {/* Original Code Panel */}
            <div className="code-panel">
              <h3 className="code-panel-title">Original Code:</h3>
              <div className="button-group">
                <button onClick={handleEditToggle} className="edit-toggle-button">
                  {isEditing ? 'View Code' : 'Edit Code'}
                </button>
                <button onClick={handleUploadToCloud} className="upload-button">
                  Upload to Cloud
                </button>
                {uploadStatus && (
                  <span className={`upload-status ${uploadStatus.includes('Error') ? 'error' : 'success'}`}>
                    {uploadStatus}
                  </span>
                )}
              </div>
              {isEditing ? (
                <pre className="file-content-pre">
                  <code
                    ref={editableCodeRef}
                    className={`${fileType} editable-code`}
                    contentEditable={true}
                    suppressContentEditableWarning={true}
                  >
                    {fileContent}
                  </code>
                </pre>
              ) : (
                <pre className="file-content-pre">
                  <code ref={codeRef} className={fileType}>
                    {fileContent}
                  </code>
                </pre>
              )}
            {fileType === 'python' && (
              <div className='python-output'>
                <button
                 className='run-button'
                 onClick={() => run_code(fileContent, "original")}
                 disabled={isCodeRunning["original"]}
                >
                 {isCodeRunning["original"] ? <LoadingAnimation /> : 'Run Code'}
                </button>
              <div className='python-content'>
                {codeOutputs["original"]}
              </div>
              </div>
           )}

          {/* Gemini Output Panel */}
          {geminiOutput && (
            <div className="code-panel">
              <h3 className="code-panel-title">Improved Code:</h3>
              <pre className="file-content-pre">
                <code ref={geminiCodeRef} className={fileType}>
                {generateDiff(fileContent, geminiOutput)}
                </code>
              </pre>
          {fileType === 'python' && (
            <div className='python-output'>
              <button
                className='run-button'
                onClick={() => run_code(geminiOutput, "improved")}
                disabled={isCodeRunning["improved"]}
              >
              {isCodeRunning["improved"] ? <LoadingAnimation /> : 'Run Code'}
              </button>
              <div className='python-content'>
                {codeOutputs["improved"]}
              </div>
            </div>
          )} 
        </div>
           )}    
        </div>
      </div>

          {/* Feedback Section */}
          {textFeedback && (
            <div className="feedback-section">
              <h3>Analysis and Feedback:</h3>
              <div className="feedback-content">
                {textFeedback}
              </div>
            </div>
          )}
               {geminiOutput && (
        <div>
          <div className="feedback-container">
            <h4 className="feeback-heading">Provide Feedback</h4>
            <textarea className="feedback-textarea"
              rows="5"
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder="Type your feedback here..."
            />
            <button className="feedback-button"onClick={handleFeedbackSubmit}>Submit Feedback</button>
            {feedbackStatus && <div className="feedback-status">{feedbackStatus}</div>}
          </div>
          {geminiFeedbackResponse && (
            <div className="feedback-output-container">
             <h3>Improved Output Based on Feedback:</h3>
             <pre>{geminiFeedbackResponse}</pre>
            </div>
          )}
        </div>
      )}

          {accuracyResults && (
            <div className="accuracy-results-container">
              <h3 className="accuracy-results-title">Accuracy Results:</h3>
              <pre className="accuracy-results-pre">
                {accuracyResults}
              </pre>
            </div>
          )}
        </div>
      )}

      {loading ? (
        <LoadingAnimation />
      ) : (
        geminiOutput && (
          <button className="geminiDownloadButton" onClick={writeToTextFile}>
            Download Gemini Result
          </button>
        )
      )}
      {fileContent && (
        <button onClick={handleTrainAndLog} className="train-log-button" disabled={isTraining}>
          {isTraining ? <LoadingAnimation /> : 'Train Model & Download Logs'}
        </button>
      )}
    </div>
  );
};

export default FileUpload;
